import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import "c3/c3.min.css";
import * as d3 from "d3";
import {
  C3ChartID,
  SVGSelecter,
  getRootElement,
  insertRangeElement,
  displayChartPoint,
  getAxisXValues,
  startRangeSelection,
  setRangeSelection,
  insertZoomTarget,
  AxisRange,
  setMousewheel,
  setXAxisRange,
  setYAxisRange,
  setZoomTarget,
  transpose,
  ChartSize,
  resizeChart,
} from "../common/chart";
import ChartDialog from "../common/ChartDialog";
import { IProtData } from "../../../../../webapi/service/getFIle";
import { AnyType, ChartDialogConfig } from "../../../../../common/constants";
import "../../../../../App.css";
import { IModelEvaluationDataRow } from "../../../../../webapi/service/getModelEvaluationData";

/**
 * C3に設定するY軸範囲を取得します
 * @param rows - チャートデータ
 * @returns Y軸範囲
 */
function getYAxisRange(rows: C3.PrimitiveArray[]): AxisRange {
  const find = rows.find((row: AnyType) => {
    return row[0] === "x";
  });
  const filter = find
    ? transpose(rows).filter((row) => {
        return row[0] !== "x";
      })
    : rows;
  const values = filter.flat().filter((val) => {
    return typeof val === "number";
  });
  const min = values.reduce((v1, v2) => {
    return Math.min(v1 as number, v2 as number);
  }) as number;
  const max = values.reduce((v1, v2) => {
    return Math.max(v1 as number, v2 as number);
  }) as number;
  return { min: min, max: max };
}

export type TrimmingChartProt = IProtData | undefined;

/**
 * マウスドラッグ情報
 */
type DragInfo = {
  /**
   * ドラッグ中の場合trueを設定します
   */
  drag: boolean;
};

/**
 * マウスクリック位置。
 * Zoomターゲットに使用
 */
type ClickPos = {
  /**
   * X座標
   */
  x: number;
  /**
   * Y座標
   */
  y: number;
};

export interface IProps extends WithTranslation {
  /**
   * チャートダイアログを表示する場合はtrue
   */
  show: boolean;
  /**
   * チャート幅
   */
  width: number;
  /**
   * チャート高さ
   */
  height: number;
  /**
   * 編集モードの有効/無効
   */
  edit: boolean;
  /**
   * 内部チャート高さ
   */
  cheight: number;
  /**
   * チャートデータ
   */
  prot: TrimmingChartProt;
  /**
   * モデル評価結果の情報
   */
  modelevalinfo?: IModelEvaluationDataRow;
  /**
   * チャートダイアログを閉じる場合に呼び出します
   */
  onCancel: () => void;
}

interface IState {
  /**
   * 編集モードにする場合はtrue
   */
  edit: boolean;
  /**
   * ファイル名入力ダイアログを表示する場合はtrue
   */
  showFileSave: boolean;
}

class TrimmingChartDialog extends React.Component<IProps, IState> {
  private readonly ChartID = "chart_trimming";
  private readonly ChartID_in = "chart_trimming_in";
  private readonly ChartID_out = "chart_trimming_out";
  private readonly C3ChartID_in = C3ChartID(this.ChartID_in);
  private readonly C3ChartID_out = C3ChartID(this.ChartID_out);
  private readonly SvcTag_in = SVGSelecter(this.C3ChartID_in);
  private readonly SvcTag_out = SVGSelecter(this.C3ChartID_out);
  private _c3: C3.ChartAPI | null = null;
  private _c3in: C3.ChartAPI | null = null;
  private _chartSize: ChartSize = { width: 0, height: 0 };
  private _chartSizeIn: ChartSize = { width: 0, height: 0 };
  private _dragInfo: DragInfo = { drag: false };
  private _contextmenuEnabled = true;
  private readonly XScaleRate = ChartDialogConfig.CHART_X_AXIS_SCALE_RATE;
  private _xZoomCount = 0;
  private _xAxisRange: AxisRange = { min: 0, max: 0 };
  private readonly YScaleRate = ChartDialogConfig.CHART_Y_AXIS_SCALE_RATE;
  private _yZoomCount = 0;
  private _yAxisRange: AxisRange = { min: 0, max: 0 };
  private _yAxisRangeIn: AxisRange = { min: 0, max: 0 };
  private _clickPos: ClickPos = { x: 0, y: 0 };

  constructor(props: IProps) {
    super(props);
    this.state = {
      edit: false,
      showFileSave: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleXAxisZoomUp = this.handleXAxisZoomUp.bind(this);
    this.handleXAxisZoomOut = this.handleXAxisZoomOut.bind(this);
    this.handleYAxisZoomUp = this.handleYAxisZoomUp.bind(this);
    this.handleYAxisZoomOut = this.handleYAxisZoomOut.bind(this);
    this.handleChartMouseup = this.handleChartMouseup.bind(this);
    this.dragInfo = this.dragInfo.bind(this);
    this.clickStart = this.clickStart.bind(this);
    this.chartSize = this.chartSize.bind(this);
    this.chartSizeIn = this.chartSizeIn.bind(this);
  }

  componentDidUpdate(prevProps: IProps) {
    const { prot, width, cheight } = this.props;
    if (!prevProps.prot && prot) {
      console.time(prot.fname);
      this.drawChart(
        prot.output,
        this.C3ChartID_out,
        { width: width, height: cheight },
        prot.datanum,
        prot.interval
      );
      this.drawChartIn(
        prot.input,
        this.C3ChartID_in,
        { width: width, height: cheight },
        prot.datanum,
        prot.interval
      );
      console.timeEnd(prot.fname);
      this._contextmenuEnabled = true;
      this._xZoomCount = 0;
      this._yZoomCount = 0;
    }
  }

  /**
   * ダイアログクローズイベント
   */
  handleClose() {
    this.setState({ edit: false });
    this.props.onCancel();
  }

  /**
   * X軸制限値プロパティ
   * @param range - X軸範囲
   * @returns X軸範囲
   */
  xAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this._xAxisRange = range;
    }
    return this._xAxisRange;
  }

  /**
   * Y軸制限値プロパティ
   * @param range - Y軸範囲
   * @returns Y軸範囲
   */
  yAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this._yAxisRange = range;
    }
    return this._yAxisRange;
  }

  /**
   * Y軸制限値プロパティ(Input)
   * @param range - Y軸範囲
   * @returns Y軸範囲
   */
  yAxisRangeIn(range?: AxisRange): AxisRange {
    if (range) {
      this._yAxisRangeIn = range;
    }
    return this._yAxisRangeIn;
  }

  /**
   * X軸拡大回数プロパティ
   * @param count - X軸拡大回数
   * @returns X軸拡大回数
   */
  xZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._xZoomCount = count;
    }
    return this._xZoomCount;
  }

  /**
   * Y軸拡大回数プロパティ
   * @param count - Y軸拡大回数
   * @returns Y軸拡大回数
   */
  yZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._yZoomCount = count;
    }
    return this._yZoomCount;
  }

  /**
   * X軸拡大率を取得します
   * @param count - X軸拡大回数
   * @returns X軸拡大率
   */
  getXRate(count: number): number {
    return count * this.XScaleRate;
  }

  /**
   * Y軸拡大率を取得します
   * @param count - Y軸拡大回数
   * @returns Y軸拡大率
   */
  getYRate(count: number): number {
    return count * this.YScaleRate;
  }

  /**
   * X軸拡大ボタンイベント
   */
  handleXAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() + 1;
      const rate = this.getXRate(count);
      if (rate <= 1) {
        setXAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * X軸縮小ボタンイベント
   */
  handleXAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() - 1;
      const rate = this.getXRate(count);
      if (rate >= 0) {
        setXAxisRange(c3, rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * Y軸拡大ボタンイベント
   */
  handleYAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() + 1;
      const rate = this.getYRate(count);
      if (rate <= 1) {
        setYAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * Y軸縮小ボタンイベント
   */
  handleYAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() - 1;
      const rate = this.getYRate(count);
      if (rate >= 0) {
        setYAxisRange(c3, rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * マウスドラッグプロパティ
   * @param dragInfo - マウスドラッグ情報
   * @returns マウスドラッグ情報
   */
  dragInfo(dragInfo?: DragInfo) {
    if (dragInfo) {
      this._dragInfo = dragInfo;
    }
    return this._dragInfo;
  }

  /**
   * マウスクリック位置プロパティ
   * @param clickPos - マウスクリック位置
   * @returns マウスクリック位置
   */
  clickStart(clickPos?: ClickPos) {
    if (clickPos) {
      this._clickPos = clickPos;
    }
    return this._clickPos;
  }

  /**
   * マウスUPイベント
   */
  handleChartMouseup() {
    this.dragInfo({ drag: false });
    window.removeEventListener("mouseup", this.handleChartMouseup);
  }

  /**
   * コンテキストメニューイベント追加
   * @param selecter コンテキストメニューイベント追加位置
   */
  addContextMenu(selecter: string): void {
    const element = document.querySelector(selecter);
    if (element) {
      element.addEventListener("contextmenu", (e) => {
        if (this._contextmenuEnabled === true) {
          return true;
        } else {
          e.preventDefault();
        }
      });
    }
  }

  /**
   * C3チャートを生成します
   * @param rows - チャートデータ
   * @param chartid - チャートID
   * @param size - チャートサイズ
   * @param maxX - X軸最大値
   * @param rangeY - Y軸範囲
   */
  generateChart(
    rows: C3.PrimitiveArray[],
    chartid: string,
    size: ChartSize,
    maxX: number,
    rangeY: AxisRange
  ) {
    this._c3 = C3.generate({
      bindto: chartid,
      size: {
        width: size.width,
        height: size.height,
      },
      data: { x: "x", rows: rows, type: "line" },
      axis: {
        x: {
          min: 0,
          max: maxX,
          label: {
            text: "[sec]",
            position: "outer-right",
          },
        },
        y: { min: rangeY.min, max: rangeY.max },
      },
      grid: {
        x: { show: ChartDialogConfig.GRID_CHARTDIALOG },
        y: { show: ChartDialogConfig.GRID_CHARTDIALOG },
      },
      padding: { right: 30, left: 70 },
      point: {
        show: false,
      },
    });
    insertRangeElement(this._c3, size.height);
    insertZoomTarget(this._c3);
  }

  /**
   * inputのC3チャートを生成します。拡大・縮小の基点が非表示になります。
   * @param rows - チャートデータ
   * @param chartid - チャートID
   * @param size - チャートサイズ
   * @param maxX - X軸最大値
   * @param rangeY - Y軸範囲
   */
  generateChartInput(
    rows: C3.PrimitiveArray[],
    chartid: string,
    size: ChartSize,
    maxX: number,
    rangeY: AxisRange
  ) {
    this._c3in = C3.generate({
      bindto: chartid,
      size: {
        width: size.width,
        height: size.height,
      },
      data: { x: "x", rows: rows, type: "line" },
      axis: {
        x: {
          min: 0,
          max: maxX,
          label: {
            text: "[sec]",
            position: "outer-right",
          },
        },
        y: { min: rangeY.min, max: rangeY.max },
      },
      grid: {
        x: { show: ChartDialogConfig.GRID_CHARTDIALOG },
        y: { show: ChartDialogConfig.GRID_CHARTDIALOG },
      },
      padding: { right: 30, left: 70 },
      point: {
        show: false,
      },
    });
  }

  /**
   * チャートサイズプロパティ
   * @param size - チャートサイズ
   * @returns チャートサイズ
   */
  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  /**
   * Inputチャートサイズプロパティ
   * @param size - チャートサイズ
   * @returns チャートサイズ
   */
  chartSizeIn(size?: ChartSize) {
    if (size) {
      this._chartSizeIn = size;
    }
    return this._chartSizeIn;
  }

  /**
   * チャートX軸Y軸を初期化します
   * @param c3 - C3
   */
  initAxisRange(c3: C3.ChartAPI) {
    const { min, max } = c3.axis.range();
    this.xAxisRange({ min: min.x, max: max.x });
    this.yAxisRange({ min: min.y, max: max.y });
  }

  /**
   * チャートX軸Y軸を初期化します（Input）
   * @param c3 - C3
   */
  initAxisRangeIn(c3: C3.ChartAPI) {
    const { min, max } = c3.axis.range();
    this.xAxisRange({ min: min.x, max: max.x });
    this.yAxisRangeIn({ min: min.y, max: max.y });
  }

  /**
   * チャートを描画します
   * @param outrows - チャートデータ
   * @param outchartid - チャートID
   * @param size - チャートサイズ
   * @param datanum - X軸データ点数
   */
  drawChart(
    outrows: C3.PrimitiveArray[],
    outchartid: string,
    size: ChartSize,
    datanum: number,
    interval: number
  ) {
    const outrangeY = getYAxisRange(outrows);
    const maxX =
      Math.round(((datanum * interval) / 1000) * Math.pow(10, 3)) /
      Math.pow(10, 3);
    if (!this._c3) {
      this.generateChart(outrows, outchartid, size, maxX, outrangeY);
      this.addContextMenu(this.SvcTag_out);
    } else {
      this._c3.destroy();
      this.generateChart(outrows, outchartid, size, maxX, outrangeY);
    }
    this.chartSize(size);
    if (this._c3) {
      this.initAxisRange(this._c3);
      displayChartPoint(this._c3, false);
      this.editChart(this._c3, false);
      setMousewheel(this._c3, this.xAxisRange(), maxX);
    }
  }

  /**
   * Inputチャートを描画します
   * @param inrows - チャートデータ
   * @param inchartid - チャートID
   * @param size - チャートサイズ
   * @param datanum - X軸データ点数
   */
  drawChartIn(
    inrows: C3.PrimitiveArray[],
    inchartid: string,
    size: ChartSize,
    datanum: number,
    interval: number
  ) {
    const inrangeY = getYAxisRange(inrows);
    const maxX =
      Math.round(((datanum * interval) / 1000) * Math.pow(10, 3)) /
      Math.pow(10, 3);
    if (!this._c3in) {
      this.generateChartInput(inrows, inchartid, size, maxX, inrangeY);
      this.addContextMenu(this.SvcTag_in);
    } else {
      this._c3in.destroy();
      this.generateChartInput(inrows, inchartid, size, maxX, inrangeY);
    }
    this.chartSizeIn(size);
    if (this._c3in) {
      this.initAxisRangeIn(this._c3in);
      displayChartPoint(this._c3in, false);
      this.editChart(this._c3in, false);
      setMousewheel(this._c3in, this.xAxisRange(), maxX);
    }
  }

  /**
   * チャートの編集可不可状態を切り替えます
   * @param c3 - C3
   * @param edit - 編集可にする場合はtrue
   */
  editChart(c3: C3.ChartAPI, edit: boolean) {
    const c3i = c3.internal;
    const root = getRootElement(c3);
    if (edit) {
      const margin = c3i.margin;
      const xs = getAxisXValues(c3);
      const onmouseup = this.handleChartMouseup;
      const dragInfo = this.dragInfo;
      const clickStart = this.clickStart;
      root
        .on("mousedown", function () {
          const event = d3.event;
          let mouseX = event.offsetX - margin.left;
          if (mouseX < 0) {
            mouseX = 0;
          }
          const mouseY = event.offsetY - margin.top;
          startRangeSelection(c3, xs, mouseX);
          dragInfo({ drag: true });
          clickStart({ x: mouseX, y: mouseY });
          window.addEventListener("mouseup", onmouseup);
        })
        .on("mousemove", function () {
          const { drag } = dragInfo();
          if (!drag) {
            return;
          }
          const event = d3.event;
          let mouseX = event.offsetX - margin.left;
          if (mouseX < 0) {
            mouseX = 0;
          }
          setRangeSelection(c3, xs, mouseX);
        })
        .on("mouseup", function () {
          const { x, y } = clickStart();
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          if (x === mouseX && y === mouseY) {
            setZoomTarget(c3, mouseX, mouseY);
          }
        })
        .on("click", null);
    } else {
      const margin = c3i.margin;
      root
        .on("mousedown", null)
        .on("mousemove", null)
        .on("mouseup", null)
        .on("click", function () {
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          setZoomTarget(c3, mouseX, mouseY);
        });
    }
  }

  render() {
    const { show, width, cheight } = this.props;
    if (this._c3) {
      resizeChart(this._c3, width, cheight, this.chartSize);      
    }
    if (this._c3in) {
      resizeChart(this._c3in, width, cheight, this.chartSizeIn);
    }
    return (
      <div>
        <ChartDialog
          open={show}
          chartid={this.ChartID}
          edit={this.state.edit}
          editMenu={false}
          onXZoomUp={this.handleXAxisZoomUp}
          onXZoomOut={this.handleXAxisZoomOut}
          onYZoomUp={this.handleYAxisZoomUp}
          onYZoomOut={this.handleYAxisZoomOut}
          onClose={this.handleClose}
          modelevalinfo={this.props.modelevalinfo}
        />
      </div>
    );
  }
}

/**
 * トリミングダイアログ
 */
export default withTranslation()(TrimmingChartDialog);
